import { DATAFEED_WS } from '../../config/index.js'

/** A compact candle row from the datafeed WS: [time(s), open, high, low, close, volume]. */
export type RawCandleRow = [number, number, number, number, number, number]

export interface HistoryParams {
  symbol: string
  timeframe: string
  /** Range start in unix seconds. Omit for count-based pagination. */
  from?: number
  /** Range end in unix seconds. */
  to: number
  /** Max bars to return (used for pan-left pagination). */
  count?: number
}

interface PendingRequest {
  resolve: (rows: RawCandleRow[]) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const REQUEST_TIMEOUT_MS = 20_000
const CONNECT_ATTEMPTS = 3
const BASE_BACKOFF_MS = 400

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Thin client for the datafeed WebSocket (`/datafeed/ws`). Manages a single
 * connection, correlates replies to requests by `reqId`, and reconnects lazily
 * with backoff when a request finds the socket closed. This is the app's only
 * WebSocket client — everything else is SSE — so it is intentionally
 * self-contained. Instantiate one per chart and call `dispose()` on unmount.
 */
export class DatafeedSocket {
  private url: string
  private ws: WebSocket | null = null
  private connecting: Promise<WebSocket> | null = null
  private nextReqId = 1
  private pending = new Map<number, PendingRequest>()
  private disposed = false

  constructor(url: string | undefined = DATAFEED_WS) {
    if (!url) throw new Error('DATAFEED_WS is not configured')
    this.url = url
  }

  /** Request a window of candles. Resolves with compact rows (possibly empty). */
  async requestHistory(params: HistoryParams): Promise<RawCandleRow[]> {
    const ws = await this.ensureConnected()
    const reqId = this.nextReqId++

    return new Promise<RawCandleRow[]>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(reqId)
        reject(new Error('Datafeed request timed out'))
      }, REQUEST_TIMEOUT_MS)

      this.pending.set(reqId, { resolve, reject, timer })
      ws.send(JSON.stringify({ reqId, type: 'history', ...params }))
    })
  }

  dispose(): void {
    this.disposed = true
    this.rejectAll(new Error('Datafeed socket disposed'))
    if (this.ws) {
      this.ws.onopen = this.ws.onmessage = this.ws.onerror = this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
  }

  private async ensureConnected(): Promise<WebSocket> {
    if (this.disposed) throw new Error('Datafeed socket disposed')
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return this.ws
    if (this.connecting) return this.connecting

    this.connecting = this.connectWithRetry()
    try {
      return await this.connecting
    } finally {
      this.connecting = null
    }
  }

  private async connectWithRetry(): Promise<WebSocket> {
    let lastErr: Error | null = null
    for (let attempt = 0; attempt < CONNECT_ATTEMPTS; attempt++) {
      if (this.disposed) throw new Error('Datafeed socket disposed')
      if (attempt > 0) await delay(BASE_BACKOFF_MS * 2 ** (attempt - 1))
      try {
        return await this.openSocket()
      } catch (err) {
        lastErr = err instanceof Error ? err : new Error(String(err))
      }
    }
    throw lastErr ?? new Error('Failed to connect to datafeed WebSocket')
  }

  private openSocket(): Promise<WebSocket> {
    return new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(this.url)
      let settled = false

      ws.onopen = () => {
        settled = true
        this.ws = ws
        resolve(ws)
      }
      ws.onmessage = (ev) => this.handleMessage(ev)
      ws.onerror = () => {
        if (!settled) {
          settled = true
          reject(new Error('Datafeed WebSocket connection error'))
        }
      }
      ws.onclose = () => {
        if (this.ws === ws) this.ws = null
        // Replies in flight can never arrive on a closed socket.
        this.rejectAll(new Error('Datafeed WebSocket closed'))
        if (!settled) {
          settled = true
          reject(new Error('Datafeed WebSocket closed before opening'))
        }
      }
    })
  }

  private handleMessage(ev: MessageEvent): void {
    let msg: { reqId?: number; type?: string; candles?: RawCandleRow[]; message?: string }
    try {
      msg = JSON.parse(ev.data)
    } catch {
      return
    }
    if (msg.reqId == null) return
    const pending = this.pending.get(msg.reqId)
    if (!pending) return

    this.pending.delete(msg.reqId)
    clearTimeout(pending.timer)

    if (msg.type === 'history') {
      pending.resolve(Array.isArray(msg.candles) ? msg.candles : [])
    } else if (msg.type === 'error') {
      pending.reject(new Error(msg.message || 'Datafeed error'))
    } else {
      pending.reject(new Error(`Unexpected datafeed reply: ${msg.type}`))
    }
  }

  private rejectAll(err: Error): void {
    for (const { reject, timer } of this.pending.values()) {
      clearTimeout(timer)
      reject(err)
    }
    this.pending.clear()
  }
}
